import { supabase } from '@/integrations/supabase/client';
import { ApiResult, ok, err, ApiError } from '../errors';
import { mutate, query } from '../client';

// ============ Types ============

export interface Craving {
  id: string;
  user_id: string;
  intensity?: number | null;
  trigger?: string | null;
  coping_strategy?: string | null;
  created_at?: string | null;
}

export interface ProgressLog {
  id: string;
  user_id: string;
  log_date: string;
  cigarettes_smoked?: number | null;
  cravings_count?: number | null;
  mood_rating?: number | null;
  stress_level?: number | null;
  anxiety_level?: number | null;
  notes?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

export interface CravingInput {
  intensity?: number;
  trigger?: string;
  coping_strategy?: string;
  created_at?: string;
}

export interface ProgressLogInput {
  log_date: string;
  cigarettes_smoked?: number;
  cravings_count?: number;
  mood_rating?: number;
  stress_level?: number;
  anxiety_level?: number;
  notes?: string;
}

// ============ Queries ============

export async function getCravings(
  userId: string,
  limit = 50,
  productId?: string
): Promise<ApiResult<Craving[]>> {
  let queryBuilder = supabase
    .from('cravings')
    .select('*')
    .eq('user_id', userId);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  return query(async () =>
    queryBuilder
      .order('created_at', { ascending: false })
      .limit(limit)
  );
}

export async function getCravingDates(
  userId: string,
  productId?: string
): Promise<ApiResult<string[]>> {
  let queryBuilder = supabase
    .from('cravings')
    .select('created_at')
    .eq('user_id', userId);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  const result = await queryBuilder;

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  const dates = (result.data || []).map(c => c.created_at?.split('T')[0] || '').filter(Boolean);
  return ok([...new Set(dates)]);
}

export async function getProgressLogs(
  userId: string,
  startDate?: string,
  endDate?: string,
  productId?: string
): Promise<ApiResult<ProgressLog[]>> {
  let queryBuilder = supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .order('log_date', { ascending: false });

  if (startDate) {
    queryBuilder = queryBuilder.gte('log_date', startDate);
  }
  if (endDate) {
    queryBuilder = queryBuilder.lte('log_date', endDate);
  }
  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  return query(async () => queryBuilder);
}

export async function getProgressLogByDate(
  userId: string,
  date: string,
  productId?: string
): Promise<ApiResult<ProgressLog | null>> {
  let queryBuilder = supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', date);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  const result = await queryBuilder.maybeSingle();

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(result.data);
}

export async function getProgressLogDates(
  userId: string,
  field: 'cigarettes_smoked' | 'mood_rating' | 'stress_level' | 'anxiety_level',
  productId?: string
): Promise<ApiResult<string[]>> {
  let queryBuilder = supabase
    .from('progress_logs')
    .select('log_date')
    .eq('user_id', userId)
    .not(field, 'is', null);

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  const result = await queryBuilder;

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  const dates = (result.data || []).map(log => log.log_date);
  return ok([...new Set(dates)]);
}

export async function getCravingsInRange(
  userId: string,
  startDate: string,
  productId?: string
): Promise<ApiResult<Array<{ created_at: string | null }>>> {
  let queryBuilder = supabase
    .from('cravings')
    .select('created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00`)
    .order('created_at', { ascending: true});

  if (productId) {
    queryBuilder = queryBuilder.eq('product_id', productId);
  }

  const result = await queryBuilder;

  if (result.error) {
    return err(ApiError.fromSupabaseError(result.error));
  }

  return ok(result.data || []);
}

// ============ Mutations ============

export async function logCraving(
  userId: string,
  input: CravingInput,
  productId?: string
): Promise<ApiResult<Craving>> {
  return mutate(async () =>
    supabase
      .from('cravings')
      .insert({
        user_id: userId,
        ...input,
        product_id: productId,
      })
      .select()
      .single()
  );
}

export async function upsertProgressLog(
  userId: string,
  input: ProgressLogInput,
  productId?: string
): Promise<ApiResult<ProgressLog>> {
  // Check if log exists for this date
  const { data: existing } = await supabase
    .from('progress_logs')
    .select('id')
    .eq('user_id', userId)
    .eq('log_date', input.log_date)
    .maybeSingle();

  if (existing) {
    // Update existing
    return mutate(async () =>
      supabase
        .from('progress_logs')
        .update({ ...input, product_id: productId })
        .eq('id', existing.id)
        .select()
        .single()
    );
  }

  // Create new
  return mutate(async () =>
    supabase
      .from('progress_logs')
      .insert({
        user_id: userId,
        ...input,
        product_id: productId,
      })
      .select()
      .single()
  );
}

export async function updateCigaretteCount(
  userId: string,
  date: string,
  count: number,
  notes?: string,
  productId?: string
): Promise<ApiResult<ProgressLog>> {
  const { data: existing } = await supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', date)
    .maybeSingle();

  if (existing) {
    return mutate(async () =>
      supabase
        .from('progress_logs')
        .update({
          cigarettes_smoked: count,
          notes: notes || existing.notes,
          product_id: productId
        })
        .eq('id', existing.id)
        .select()
        .single()
    );
  }

  return mutate(async () =>
    supabase
      .from('progress_logs')
      .insert({
        user_id: userId,
        log_date: date,
        cigarettes_smoked: count,
        notes: notes || null,
        product_id: productId,
      })
      .select()
      .single()
  );
}

export async function updateMoodRating(
  userId: string,
  date: string,
  rating: number,
  notes?: string,
  productId?: string
): Promise<ApiResult<ProgressLog>> {
  const { data: existing } = await supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', date)
    .maybeSingle();

  if (existing) {
    return mutate(async () =>
      supabase
        .from('progress_logs')
        .update({
          mood_rating: rating,
          notes: notes || existing.notes,
          product_id: productId
        })
        .eq('id', existing.id)
        .select()
        .single()
    );
  }

  return mutate(async () =>
    supabase
      .from('progress_logs')
      .insert({
        user_id: userId,
        log_date: date,
        mood_rating: rating,
        notes: notes || null,
        product_id: productId,
      })
      .select()
      .single()
  );
}

export async function updateStressLevel(
  userId: string,
  date: string,
  level: number,
  notes?: string,
  productId?: string
): Promise<ApiResult<ProgressLog>> {
  const { data: existing } = await supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', date)
    .maybeSingle();

  if (existing) {
    return mutate(async () =>
      supabase
        .from('progress_logs')
        .update({
          stress_level: level,
          notes: notes || existing.notes,
          product_id: productId
        })
        .eq('id', existing.id)
        .select()
        .single()
    );
  }

  return mutate(async () =>
    supabase
      .from('progress_logs')
      .insert({
        user_id: userId,
        log_date: date,
        stress_level: level,
        notes: notes || null,
        product_id: productId,
      })
      .select()
      .single()
  );
}

export async function updateAnxietyLevel(
  userId: string,
  date: string,
  level: number,
  notes?: string,
  productId?: string
): Promise<ApiResult<ProgressLog>> {
  const { data: existing } = await supabase
    .from('progress_logs')
    .select('*')
    .eq('user_id', userId)
    .eq('log_date', date)
    .maybeSingle();

  if (existing) {
    return mutate(async () =>
      supabase
        .from('progress_logs')
        .update({
          anxiety_level: level,
          notes: notes || existing.notes,
          product_id: productId
        })
        .eq('id', existing.id)
        .select()
        .single()
    );
  }

  return mutate(async () =>
    supabase
      .from('progress_logs')
      .insert({
        user_id: userId,
        log_date: date,
        anxiety_level: level,
        notes: notes || null,
        product_id: productId,
      })
      .select()
      .single()
  );
}
